import { useFormik } from "formik";
import { isValid as isIBANValid } from "iban";
import { Divider } from "primereact/divider";
import { forwardRef, useImperativeHandle } from "react";
import { useFragment } from "react-relay";
import * as Yup from "yup";
import { type BusinessType, BusinessTypeDropdown } from "@components/business-type-dropdown";
import { InputMask } from "@components/input-mask";
import { useTranslation } from "@i18n/hooks";
import { CountryCode } from "@relay/accountEditBaseDataScreen_EditBusinessBaseDataMutation.graphql";
import { QUERY_FRAGMENT } from "./business-base-data-form.graphql";
import {
	ColSpan2,
	ColSpan4,
	InputGroupWrapper,
	ShortInputsRow,
	Wrapper,
} from "./business-base-data-form.styles";
import type {
	BusinessBaseDataFormState,
	BusinessBaseDataProps,
	BusinessBaseDataRef,
} from "./business-base-data-form.types";
import { DefaultTextFieldComponent } from "../../../../components/DefaultTextInput";
import { ResidenceDropdown } from "../../../../components/residence-dropdown";
import { ValidatedField } from "../../../../components/ValidatedField";

export const BusinessBaseDataForm = forwardRef<BusinessBaseDataRef, BusinessBaseDataProps>(
	function BusinessBaseDataForm({ baseDataFragmentRef, onSubmit }, ref) {
		const { t } = useTranslation(["common", "validation"]);

		const query = useFragment(QUERY_FRAGMENT, baseDataFragmentRef ?? null);

		const form = useFormik<BusinessBaseDataFormState>({
			initialValues: {
				companyName: query?.companyName ?? "",
				billingOffice: query?.billingOffice ?? "",
				companyLegalForm: query?.companyLegalForm ?? undefined,
				invoiceEmail: query?.invoiceEmail ?? "",
				street: query?.street ?? "",
				houseNumber: query?.houseNumber ?? "",
				postalCode: query?.postalCode ?? "",
				city: query?.city ?? "",
				countryCode: query?.countryCode ?? "DE",
				phoneNumber: query?.phoneNumber ?? "",
				taxNumber: query?.taxData?.taxNumber ?? "",
				taxIdentificationNumber: query?.taxData?.taxIdentificationNumber ?? "",
				iban: query?.accountData?.iban ?? "",
				bic: query?.accountData?.bic ?? "",
			},
			validateOnChange: false,
			validateOnBlur: false,
			validationSchema: Yup.object().shape({
				companyName: Yup.string().required(t("validation:required")),
				billingOffice: Yup.string().required(t("validation:required")),
				companyLegalForm: Yup.string().required(t("validation:required")),
				invoiceEmail: Yup.string()
					.email(t("validation:email"))
					.required(t("validation:required")),
				street: Yup.string().required(t("validation:required")),
				houseNumber: Yup.string().required(t("validation:required")),
				city: Yup.string().required(t("validation:required")),
				postalCode: Yup.string()
					.length(5, t("validation:postalCode"))
					.matches(/^[0-9]{5}/, t("validation:postalCode"))
					.required(t("validation:required")),
				phoneNumber: Yup.string().optional(),
				countryCode: Yup.string().required(t("validation:required")),
				iban: Yup.string()
					.test("test-card-number", t("validation:iban"), (value) =>
						isIBANValid(value ?? ""),
					)
					.required(t("validation:required")),
				bic: Yup.string(),
				taxNumber: Yup.string().required(t("validation:required")),
				taxIdentificationNumber: Yup.string().required(t("validation:required")),
			}),
			onSubmit: (values) => {
				onSubmit?.(values);
			},
		});

		useImperativeHandle(ref, () => ({
			submit: form.submitForm,
			validate: () => form.validateForm().then((errors) => !errors),
		}));

		return (
			<Wrapper>
				<InputGroupWrapper>
					<ValidatedField<BusinessBaseDataFormState, string>
						formikConfig={form}
						name="companyName"
						label={t("common:accounts.companyName")}
						placeholder={t("common:placeholders.companyName")}
						component={DefaultTextFieldComponent}
					/>
					<ValidatedField<BusinessBaseDataFormState, string>
						formikConfig={form}
						name="billingOffice"
						label={t("common:accounts.billingOffice")}
						placeholder={t("common:placeholders.billingOffice")}
						component={DefaultTextFieldComponent}
					/>
					<ValidatedField<BusinessBaseDataFormState, BusinessType>
						formikConfig={form}
						name="companyLegalForm"
						label={t("common:accounts.legalForm")}
						placeholder={t("common:placeholders.legalForm")}
						component={BusinessTypeDropdown}
					/>
					<ValidatedField<BusinessBaseDataFormState, string>
						formikConfig={form}
						name="invoiceEmail"
						label={t("common:accounts.invoiceEmail")}
						placeholder="..."
						component={DefaultTextFieldComponent}
					/>
					<ShortInputsRow>
						<ColSpan4>
							<ValidatedField<BusinessBaseDataFormState, string>
								formikConfig={form}
								name="street"
								label={t("common:accounts.street")}
								placeholder="..."
								component={DefaultTextFieldComponent}
							/>
						</ColSpan4>
						<ColSpan2>
							<ValidatedField<BusinessBaseDataFormState, string>
								formikConfig={form}
								name="houseNumber"
								label={t("common:accounts.houseNumber")}
								placeholder="..."
								component={DefaultTextFieldComponent}
							/>
						</ColSpan2>
					</ShortInputsRow>
					<ShortInputsRow>
						<ColSpan4>
							<ValidatedField<BusinessBaseDataFormState, string>
								formikConfig={form}
								name="city"
								label={t("common:accounts.city")}
								placeholder="..."
								component={DefaultTextFieldComponent}
							/>
						</ColSpan4>
						<ColSpan2>
							<ValidatedField<BusinessBaseDataFormState, string>
								formikConfig={form}
								name="postalCode"
								label={t("common:accounts.postalCode")}
								placeholder="..."
								component={DefaultTextFieldComponent}
							/>
						</ColSpan2>
					</ShortInputsRow>
					<ValidatedField<BusinessBaseDataFormState, string>
						formikConfig={form}
						name="phoneNumber"
						label={t("common:accounts.phoneNumber")}
						placeholder={t("common:placeholders.phone")}
						component={DefaultTextFieldComponent}
					/>
					<ValidatedField<BusinessBaseDataFormState, CountryCode>
						formikConfig={form}
						name="countryCode"
						label={t("common:accounts.taxCountry")}
						component={ResidenceDropdown}
					/>
				</InputGroupWrapper>
				<Divider />
				<h2 className="m-0">{t("common:accounts.taxData")}</h2>
				<InputGroupWrapper>
					<InputMask<BusinessBaseDataFormState>
						formikConfig={form}
						name="taxNumber"
						label={t("common:accounts.taxNumber")}
						placeholder={t("common:placeholders.taxNumber")}
						mask={"99/999/9999?*"}
					/>
					<InputMask<BusinessBaseDataFormState>
						formikConfig={form}
						name="taxIdentificationNumber"
						label={t("common:accounts.vatId")}
						placeholder={t("common:placeholders.vatId")}
						mask={"DE999999999"}
					/>
				</InputGroupWrapper>
				<Divider />
				<h2 className="m-0">{t("common:accounts.bankData")}</h2>
				<InputGroupWrapper>
					<ValidatedField<BusinessBaseDataFormState, string>
						formikConfig={form}
						name="iban"
						label={t("common:accounts.iban")}
						placeholder={t("common:placeholders.iban")}
						component={DefaultTextFieldComponent}
					/>
					<ValidatedField<BusinessBaseDataFormState, string>
						formikConfig={form}
						name="bic"
						label={t("common:accounts.bic")}
						placeholder={t("common:placeholders.bic")}
						component={DefaultTextFieldComponent}
					/>
				</InputGroupWrapper>
			</Wrapper>
		);
	},
);
